/**
 * PARAMÈTRES › INDEXATION CARBURANT — l'indice de référence de la société et
 * le taux du mois.
 *
 * Écran ouvert le 15/09/2026 pour publier « le taux du mois » (maquette
 * `docs/maquettes/tarifs.html`, écran 5). Remanié les 02/11/2026 (lots 136
 * puis 137) sur la règle de Mohamed : « il faut juste qu'on inscrive notre
 * indice de référence et le taux du mois, après le logiciel fait ses
 * calculs » — « tu utilises l'indice de base et tu rajoutes la différence de
 * prix en indexation ».
 *
 *
 * DEUX SAISIES, ET LE LOGICIEL CALCULE
 * ---------------------------------------------------------------------------
 *   - L'INDICE DE RÉFÉRENCE DE LA SOCIÉTÉ, daté : il vaut à partir de sa date
 *     et jusqu'au suivant ;
 *   - LE TAUX DU MOIS, publié chaque mois.
 *
 * Le tableau des mois montre la base, l'indexation (taux − base) et le prix du
 * litre qui sert au coût : base + indexation, c'est-à-dire le taux du mois.
 * Mohamed a lu avant le code que le prix vaut toujours le taux.
 *
 * Le prix au litre de référence qu'avait ajouté le lot 136 a disparu : il
 * n'était pas demandé.
 *
 *
 * LE BANDEAU AU-DESSUS DES MOIS : `indices_gazole` ne porte qu'UNE LIGNE PAR
 * MOIS pour toutes les sociétés. Publier un taux écrit donc ce que toutes
 * liront. L'indice de référence, lui, est propre à la société.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  effacerReferenceCarburant,
  lireIndexationCarburant,
  poserReferenceCarburant,
  publierLeGazole,
  type EchecApi,
  type IndiceGazole,
  type ReferenceCarburantLue,
  type Resultat,
} from './api.js';
import { enQuatreDecimales, moisDit } from './finance.js';
import { instantDit } from './ot.js';
// LE CONTRÔLE DE SAISIE VIENT DE L'ÉCRAN TARIFS, et n'est pas recopié ici.
// `fauteDeMontant` et `enNombre` y sont exportés et éprouvés par
// `tarifs-montant.test.ts` — nettoyage de l'espace insécable, de l'euro, du
// pour-cent, et refus d'une décimale de trop. En recopier une seconde version
// garantirait qu'elles divergent au premier correctif porté d'un seul côté.
import { enNombre, fauteDeMontant } from './EcranTarifs.js';

/** « 2026-09 » pour le mois courant. */
function moisCourant(): string {
  return new Date().toISOString().slice(0, 7);
}

/** « 2026-11-02 » pour aujourd'hui. */
function jourCourant(): string {
  return new Date().toISOString().slice(0, 10);
}

/** « 2026-11-02 » → « 02/11/2026 ». */
function jourDit(jour: string): string {
  const [a, m, j] = jour.split('-');
  return a === undefined || m === undefined || j === undefined ? jour : `${j}/${m}/${a}`;
}

/**
 * « +0,0065 (+0,50 %) » — l'indexation du mois par rapport à la base. Un tiret
 * quand elle ne se calcule pas (pas de taux, ou pas d'indice de référence).
 */
export function indexationDite(indexation: string | null, base: string | null): string {
  if (indexation === null || base === null) return '\u2014';
  const n = Number(indexation);
  const b = Number(base);
  if (!Number.isFinite(n) || !Number.isFinite(b) || b <= 0) return '\u2014';
  const signe = n > 0 ? '+' : n < 0 ? '\u2212' : '';
  const valeur = Math.abs(n).toFixed(4).replace('.', ',');
  const pct = Math.abs((n / b) * 100).toFixed(2).replace('.', ',');
  return `${signe}${valeur} (${signe}${pct} %)`;
}

export type EtatGazole =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'panne' }
  | {
      readonly nom: 'prete';
      readonly indices: readonly IndiceGazole[];
      readonly references: readonly ReferenceCarburantLue[];
    };

export interface SaisieGazole {
  readonly mois: string;
  readonly taux: string;
  readonly refIndice: string;
  readonly refDu: string;
}

export interface ProprietesVueGazole {
  readonly etat: EtatGazole;
  readonly saisie: SaisieGazole;
  readonly refus: string | null;
  readonly fait: string | null;
  readonly envoi: boolean;
  readonly quandChamp: (champ: keyof SaisieGazole, valeur: string) => void;
  readonly quandPublier: () => void;
  readonly quandPoserReference: () => void;
  readonly quandEffacerReference: (id: string) => void;
}

export function OngletIndexationCarburant({
  jeton,
  quandAccesPerdu,
  messageRefus,
}: {
  /**
   * LE JETON EST UNE FONCTION et non une chaîne : il ne vaut qu'une heure. Un
   * écran de paramètres est de ceux qu'on laisse ouverts.
   */
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string | undefined;
}) {
  const [etat, setEtat] = useState<EtatGazole>({ nom: 'chargement' });
  const [saisie, setSaisie] = useState<SaisieGazole>({
    mois: moisCourant(),
    taux: '',
    refIndice: '',
    refDu: jourCourant(),
  });
  const [refus, setRefus] = useState<string | null>(null);
  const [fait, setFait] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  const recharger = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    const resultat = await lireIndexationCarburant(j);
    // UNE LECTURE QUI ÉCHOUE LE DIT. Rendre une liste vide ferait lire
    // « aucun mois publié » là où c'est le serveur qui n'a pas répondu — et
    // Mohamed republierait par-dessus ce qui existe.
    setEtat(
      resultat.etat === 'ok'
        ? { nom: 'prete', ...resultat.valeur }
        : { nom: 'panne' },
    );
  }, [avecJeton]);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  /** Un envoi : le refus de l'API passe avant le nôtre (acquis du lot 77). */
  const envoyer = (
    action: (j: string) => Promise<Resultat<unknown>>,
    reussite: string,
    apres: () => void,
  ) => {
    setEnvoi(true);
    void (async () => {
      const j = await avecJeton();
      if (j === null) {
        setEnvoi(false);
        return;
      }
      const resultat = await action(j);
      setEnvoi(false);
      if (resultat.etat === 'ok') {
        setFait(reussite);
        apres();
        await recharger();
        return;
      }
      setRefus(
        resultat.message ??
          messageRefus(resultat.cause) ??
          'Stafa a refusé cet enregistrement. Réessayez ou rechargez l’écran.',
      );
    })();
  };

  const publier = () => {
    setRefus(null);
    setFait(null);
    if (!/^[0-9]{4}-(0[1-9]|1[0-2])$/.test(saisie.mois)) {
      setRefus('Le mois s’écrit AAAA-MM, par exemple 2026-09.');
      return;
    }
    // LE TAUX EST OBLIGATOIRE : c'est lui qui fait le prix du litre du coût
    // du gazole, en plus de l'indexation des ventes.
    if (saisie.taux.trim() === '') {
      setRefus('Le taux du mois est obligatoire.');
      return;
    }
    const faute = fauteDeMontant(saisie.taux, 'Taux du mois', 4);
    if (faute !== null) {
      setRefus(faute);
      return;
    }
    envoyer(
      (j) => publierLeGazole(j, { mois: saisie.mois, indice: enNombre(saisie.taux) }),
      `${moisDit(saisie.mois)} publié.`,
      () => {
        setSaisie((s) => ({ ...s, taux: '' }));
      },
    );
  };

  const poserReference = () => {
    setRefus(null);
    setFait(null);
    if (saisie.refIndice.trim() === '') {
      setRefus('L’indice de référence est obligatoire.');
      return;
    }
    const faute = fauteDeMontant(saisie.refIndice, 'Indice de référence', 4);
    if (faute !== null) {
      setRefus(faute);
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(saisie.refDu)) {
      setRefus('La date de la référence est obligatoire.');
      return;
    }
    envoyer(
      (j) =>
        poserReferenceCarburant(j, {
          indice: enNombre(saisie.refIndice),
          du: saisie.refDu,
        }),
      `Indice de référence du ${jourDit(saisie.refDu)} enregistré.`,
      () => {
        setSaisie((s) => ({ ...s, refIndice: '' }));
      },
    );
  };

  const effacerReference = (id: string) => {
    setRefus(null);
    setFait(null);
    envoyer((j) => effacerReferenceCarburant(j, id), 'Indice de référence effacé.', () => {});
  };

  return (
    <VueIndexationCarburant
      etat={etat}
      saisie={saisie}
      refus={refus}
      fait={fait}
      envoi={envoi}
      quandChamp={(champ, valeur) => {
        setSaisie((s) => ({ ...s, [champ]: valeur }));
      }}
      quandPublier={publier}
      quandPoserReference={poserReference}
      quandEffacerReference={effacerReference}
    />
  );
}

/**
 * LE DESSIN, SANS SERVEUR NI JETON.
 *
 * SÉPARÉ DU CONTENEUR pour la raison qui a coûté cher le 11/09 : un écran
 * qu'on ne rend pas est un écran que personne ne relit. Cette vue se rend dans
 * `apercu.tsx` (`?ecran=indexation-carburant`), donc elle se mesure.
 */
export function VueIndexationCarburant({
  etat,
  saisie,
  refus,
  fait,
  envoi,
  quandChamp,
  quandPublier,
  quandPoserReference,
  quandEffacerReference,
}: ProprietesVueGazole) {
  const indices = etat.nom === 'prete' ? etat.indices : [];
  const references = etat.nom === 'prete' ? etat.references : [];

  return (
    <div className="parametres">
      {refus === null ? null : <p className="bandeau bandeau-refus">{refus}</p>}
      {fait === null ? null : <p className="attente">{fait}</p>}

      {/* 1. L'INDICE DE RÉFÉRENCE DE LA SOCIÉTÉ */}
      <h3 className="titre-indexation">Notre indice de référence</h3>
      <form
        className="barre-liste-reglee"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          quandPoserReference();
        }}
      >
        <span className="decompte">
          {etat.nom === 'prete'
            ? references.length === 0
              ? 'Aucun indice de référence'
              : `${references.length} indice${references.length > 1 ? 's' : ''} de référence`
            : ''}
        </span>
        <input
          className="champ"
          type="text"
          inputMode="decimal"
          aria-label="Indice de référence"
          placeholder="Indice de référence — 1,2875"
          value={saisie.refIndice}
          disabled={envoi}
          onChange={(evenement) => quandChamp('refIndice', evenement.target.value)}
        />
        <input
          className="champ"
          type="date"
          aria-label="À partir du"
          value={saisie.refDu}
          disabled={envoi}
          onChange={(evenement) => quandChamp('refDu', evenement.target.value)}
        />
        <button className="bouton" type="submit" disabled={envoi}>
          Enregistrer
        </button>
      </form>

      <div className="tableau-cadre">
        <table>
          <thead>
            <tr>
              <th>À partir du</th>
              <th>Indice de référence</th>
              <th aria-label="Effacer" />
            </tr>
          </thead>
          <tbody>
            {references.length === 0 && etat.nom === 'prete' && (
              <tr>
                <td colSpan={3} className="rien">
                  Aucun indice de référence.
                </td>
              </tr>
            )}
            {references.map((r) => (
              <tr key={r.id}>
                <td>{jourDit(r.du)}</td>
                <td className="code">{enQuatreDecimales(r.indice)}</td>
                <td>
                  <button
                    type="button"
                    className="lien-action"
                    disabled={envoi}
                    onClick={() => {
                      quandEffacerReference(r.id);
                    }}
                  >
                    Effacer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 2. LE TAUX DU MOIS — commun à toutes les sociétés */}
      <h3 className="titre-indexation">Taux du mois</h3>
      <form
        className="barre-liste-reglee"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          quandPublier();
        }}
      >
        <span className="decompte">
          {etat.nom === 'chargement'
            ? 'Chargement…'
            : etat.nom === 'panne'
              ? 'Stafa ne répond pas.'
              : `${indices.length} mois publié${indices.length > 1 ? 's' : ''}`}
        </span>
        <input
          className="champ court"
          type="month"
          aria-label="Mois"
          value={saisie.mois}
          disabled={envoi}
          onChange={(evenement) => quandChamp('mois', evenement.target.value)}
        />
        <input
          className="champ"
          type="text"
          inputMode="decimal"
          aria-label="Taux du mois"
          placeholder="Taux — 1,2940"
          value={saisie.taux}
          disabled={envoi}
          onChange={(evenement) => quandChamp('taux', evenement.target.value)}
        />
        <button className="bouton" type="submit" disabled={envoi}>
          Publier le mois
        </button>
      </form>

      <p className="bandeau bandeau-attente">
        <b>Le taux du mois est commun à toutes les sociétés de Stafa.</b> Ce que
        vous publiez ici, toutes les sociétés le liront.
      </p>

      <div className="tableau-cadre">
        <table>
          <thead>
            <tr>
              <th>Mois</th>
              <th>Taux du mois</th>
              <th>Indice de référence</th>
              <th>Indexation</th>
              <th>Prix du litre (coût)</th>
              <th>Publié le</th>
            </tr>
          </thead>
          <tbody>
            {indices.length === 0 && etat.nom === 'prete' && (
              <tr>
                <td colSpan={6} className="rien">
                  Aucun mois publié pour l’instant.
                </td>
              </tr>
            )}
            {indices.map((i) => (
              <tr key={i.mois}>
                <td>{moisDit(i.mois)}</td>
                <td className="code">
                  {i.indice === null ? (
                    <span className="attente">absent</span>
                  ) : (
                    enQuatreDecimales(i.indice)
                  )}
                </td>
                <td className="code">{enQuatreDecimales(i.indice_reference ?? null)}</td>
                {/* L'INDEXATION = TAUX − BASE, signée, avec son pourcentage. */}
                <td className="code">{indexationDite(i.indexation ?? null, i.indice_reference ?? null)}</td>
                {/* LE PRIX QUI SERT AU COÛT : base + indexation = le taux. */}
                <td className="code">
                  {i.prix_calcule === null ? (
                    <span className="attente">taux absent</span>
                  ) : (
                    `${enQuatreDecimales(i.prix_calcule)} €`
                  )}
                </td>
                {/* EN FRANÇAIS, comme partout ailleurs dans Stafa — corrigé au
                    rendu : `.slice(0, 10)` affichait « 2026-09-01 ». */}
                <td>{instantDit(i.publie_le) ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
